import {
  Configuration,
  ImpersonatorAccount,
} from '@givz/core-client/dist/rest-client';
import { useAuthContext, useCoreClient } from '@hooks';
import { notificationErrorState, selectedCampaignGidState } from '@states';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  impersonateAccessTokenState,
  impersonateAccountState,
  impersonateIsLoggedInState,
} from 'src/states/impersonate';
import { useRouter } from 'next/router';
import { Routes } from '@routes';
import { useIsImpersonator } from '@hooks/auth';

export const useImpersonate = () => {
  const [isLoggedIn, setIsLoggedIn] = useRecoilState(
    impersonateIsLoggedInState,
  );
  const [accessToken, setAccessToken] = useRecoilState(
    impersonateAccessTokenState,
  );
  const [impersonateAccount, setImpersonateAccount] = useRecoilState(
    impersonateAccountState,
  );
  const [, setSelectedCampaignGid] = useRecoilState(selectedCampaignGidState);
  const logOut = useCallback(() => {
    setIsLoggedIn(false);
    setAccessToken(undefined);
    setImpersonateAccount(undefined);
    setSelectedCampaignGid(undefined);
  }, [
    setAccessToken,
    setImpersonateAccount,
    setIsLoggedIn,
    setSelectedCampaignGid,
  ]);

  const logIn = useCallback(
    (accessToken: string) => {
      setIsLoggedIn(true);
      setAccessToken(accessToken);
    },
    [setAccessToken, setIsLoggedIn],
  );

  return {
    accessToken,
    impersonateAccount,
    isLoggedIn,
    logIn,
    logOut,
    setAccessToken,
    setImpersonateAccount,
    setIsLoggedIn,
  };
};

export const useFetchAccounts = () => {
  const coreClient = useCoreClient();
  const [loading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState<ImpersonatorAccount[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [, setNotificationError] = useRecoilState(notificationErrorState);

  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoading(true);
      const { data, errors } = await coreClient().Impersonator.listAccounts({
        q: searchValue,
      });
      setIsLoading(false);
      if (errors) {
        setNotificationError(errors);
      } else {
        setAccounts(data.accounts);
      }
    };

    if (searchValue) {
      fetchAccounts();
    }
  }, [coreClient, searchValue, setNotificationError]);

  return {
    accounts,
    loading,
    searchValue,
    setSearchValue,
  };
};

export const useFetchImpersonateAccessToken = (gid?: string) => {
  const authContext = useAuthContext();
  const [loading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string>();
  const [, setNotificationError] = useRecoilState(notificationErrorState);

  const coreClient = useCoreClient({
    accessToken: authContext?.accessToken,
  } as Configuration);

  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoading(true);
      const { data, errors } = await coreClient().Impersonator.impersonate({
        impersonatorImpersonateDto: { targetAccountGid: gid! },
      });
      setIsLoading(false);
      if (errors) {
        setNotificationError(errors);
      } else {
        setAccessToken(data.accessToken);
      }
    };
    if (gid) {
      fetchAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid, setNotificationError]);

  return {
    accessToken,
    loading,
  };
};

export const useAuthenticateImpersonateAccount = (gid?: string) => {
  const { accessToken, loading } = useFetchImpersonateAccessToken(gid);
  const { logIn } = useImpersonate();

  useEffect(() => {
    if (accessToken) {
      logIn(accessToken);
    }
  }, [accessToken, logIn]);

  return {
    accessToken,
    loading,
  };
};

export const useRedirectToImpersonatePage = () => {
  const isImpersonator = useIsImpersonator();
  const router = useRouter();
  const impersonateAccount = useRecoilValue(impersonateAccountState);

  useEffect(() => {
    const routeChangeStart = (url: string) => {
      if (isImpersonator && !impersonateAccount && url !== Routes.impersonate) {
        router.push(Routes.impersonate);
      }
    };

    router.events.on('routeChangeStart', routeChangeStart);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
    };
  }, [isImpersonator, router.events, impersonateAccount, router]);

  useEffect(() => {
    if (
      isImpersonator &&
      !impersonateAccount &&
      router.asPath !== Routes.impersonate
    ) {
      router.push(Routes.impersonate);
    }
  }, [isImpersonator, impersonateAccount, router.asPath, router]);
};
